import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Button, FormGroup, Label, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { fetchEmployeeDropdown, fetchActiveEmployees, getMonthlyPFList, clearMonthlyPFList } from "store/actions";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import * as Yup from "yup";
import Select from "react-select";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomDatePicker from "common/CustomDatePicker";
import TableContainerColumnTotal from "components/Common/TableContainerColumnTotal";
import CustomMonthPicker from "common/CustomMonthPicker";
import CustomSelect from "components/Common/custom-select";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";
import { orderBy } from "lodash";
import Swal from 'sweetalert2';

const orderByOptions = [
  { label: "ESI Number", value: "1" },
  { label: "Name", value: "2" },
  { label: "Date of Joining", value: "3" },
];

const PayrollReportMonthlyPF = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prev) => !prev);

  const {
    dropdownData,
    employees,
    monthlyPFList,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    dropdownData: state.EmployeeDetailsReducer?.dropdownData ?? {},
    employees: state.EmployeeDetailsReducer?.employeesActiveList ?? [],
    monthlyPFList: state.PayrollReportsReducer?.monthlyPFList ?? [],
    loading: state.PayrollReportsReducer?.loading,
    error: state.PayrollReportsReducer?.error,
    success: state.PayrollReportsReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0]?.unit_id
    dispatch(fetchEmployeeDropdown(unitId));
    dispatch(fetchActiveEmployees(unitId));
  }, [dispatch]);

  const categoryOptions = dropdownData?.category_list?.map((item) => ({ label: item.category_name, value: item.category_id, })) || [];
  const departmentOptions = dropdownData?.department_list?.map((item) => ({ label: item.depart_name, value: item.depart_id, })) || [];
  const designationOptions = dropdownData?.designation_list?.map((item) => ({ label: item.desig_name, value: item.desig_id, })) || [];

  const formik = useFormik({
    initialValues: {
      category: "0",
      department: "0",
      designation: "0",
      month: "",
      employee: "",
      orderBy: "0",
      employeesActiveList: [],
      monthlyPFList: [],
      loading: false,
      error: null,
      success: false,
      dropdownData: {},
    },
    validationSchema: Yup.object({
      month: Yup.string().required("Month is required"),
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("unit_id", authDetails?.unit_id || 0);
      formData.append("emp_cat", values.category || 0);
      formData.append("emp_dep", values.department || 0);
      formData.append("design", values.designation || 0);
      formData.append("month", values.month || "");
      formData.append("emp_code", values.employee || 0);
      formData.append("order_by", values.orderBy || 0);
      dispatch(getMonthlyPFList(formData));
    },
  });

  const columns = [
    { Header: "S.NO", Cell: ({ row }) => monthlyPFList.length - row.index, className: "serial-no", applyHeaderClass: true },
    { Header: "CODE", accessor: "code" },
    { Header: "NAME", accessor: "name" },
    { Header: "GENDER", accessor: "gender" },
    { Header: "DOJ", accessor: "date_of_joining" },
    { Header: "CATEGORY", accessor: "category" },
    { Header: "DESIGNATION", accessor: "designation" },
    { Header: "DEPARTMENT", accessor: "department" },
    { Header: "SALARY_MONTH", accessor: "salary_month" },
    { Header: "PF_NUMBER", accessor: "pf_number" },
    { Header: "PAID_DAYS", accessor: "net_paid_days", sum: true },
    { Header: "GROSS_ACTUAL", accessor: "gross_earning(actual)", sum: true },
    { Header: "GROSS_EARNINGS", accessor: "gross_earning", sum: true },
    { Header: "PF_GROSS", accessor: "pf_gross", sum: true },
    { Header: "PENSION", accessor: "pension_gross" },
    { Header: "PF_PERCENTAGE", accessor: "employee_pf_per", sum: true },
    { Header: "PF_AMOUNT", accessor: "employee_pf_amount", sum: true },
    { Header: "PENSION_PERCENTAGE", accessor: "employers_pension_cont_per", sum: true },
    { Header: "PENSION_AMOUNT", accessor: "employers_pension_cont", sum: true },
    { Header: "EPF_PERCENTAGE", accessor: "employers_epfdiff_per", sum: true },
    { Header: "EPF_AMOUNT", accessor: "employers_epfdiff", sum: true },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Payroll / Reports / Salary Reports" breadcrumbItem="Monthly PF Register" />
        <Card>
          <CardBody>
            <form onSubmit={formik.handleSubmit}>
              <Row className="gy-3">
                <Col md={4}>
                  <Label>Category</Label>
                  <Select
                    options={categoryOptions}
                    name="category"
                    value={categoryOptions.find((opt) => opt.value === formik.values.category) || null}
                    onChange={(selected) => formik.setFieldValue("category", selected ? selected.value : "")}
                    placeholder="Select"
                  />
                </Col>
                <Col md={4}>
                  <Label>Department</Label>
                  <Select
                    options={departmentOptions}
                    name="department"
                    value={departmentOptions.find((opt) => opt.value === formik.values.department) || 0}
                    onChange={(selected) => formik.setFieldValue("department", selected ? selected.value : 0)}
                    placeholder="Select"
                  />
                </Col>
                <Col md={4}>
                  <Label>Designation</Label>
                  <Select
                    options={designationOptions}
                    name="designation"
                    value={designationOptions.find((opt) => opt.value === formik.values.designation) || null}
                    onChange={(selected) => formik.setFieldValue("designation", selected ? selected.value : "")}
                    placeholder="Select"
                  />
                </Col>
                <Col md={4}>
                  <Label>Employee Code</Label>
                  <CustomSelect
                    id="employee"
                    name="employee"
                    isMulti={false}
                    options={employees || []}
                    value={employees?.find(opt => opt.value === formik.values.employee) || null}
                    onChange={(selectedOption) =>
                      formik.setFieldValue("employee", selectedOption ? selectedOption.value : "")
                    }
                    onBlur={() => formik.setFieldTouched("employee", true)}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isClearable={true}
                  />
                </Col>
                <Col md={4}>
                  <Label>Month <span className="text-danger">*</span></Label>
                  <CustomMonthPicker
                    name="month"
                    value={formik.values.month}
                    onChange={(date) => { formik.setFieldValue("month", date); }}
                    onBlur={() => { console.log("Blurred! Value is:", formik.values.month); }}
                    disableFuture={true}
                    className="form-control form-control-sm"
                    classNamePrefix="react-select"
                    isClearable={true}
                  />
                  {formik.touched.month && formik.errors.month && (
                    <div className="text-danger mt-1">{formik.errors.month}</div>
                  )}
                </Col>
                <Col md={4}>
                  <Label>Order By</Label>
                  <Select
                    options={orderByOptions}
                    name="orderBy"
                    value={orderByOptions.find(opt => opt.value === formik.values.orderBy) || null}
                    onChange={selected => formik.setFieldValue("orderBy", selected ? selected.value : "")}
                    placeholder="Select"
                  />
                </Col>
                <Col className="d-flex align-items-end justify-content-end">
                  <Button type="submit" className="btn button-search me-2"> <i className="fas fa-search me-1" /> Search</Button>
                  {monthlyPFList && monthlyPFList.length > 0 && (
                    <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                      <DropdownToggle color="primary" className="btn button-export me-2 ms-2"> <i className="fas fa-file-export me-1" /> Export</DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem onClick={() => exportEmployeeToExcel(monthlyPFList, authDetails?.unit_fullname, authDetails?.unit_address, "Section Wage Register Report")}>
                          <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                        </DropdownItem>
                        <DropdownItem onClick={() => exportEmployeeToPDF(monthlyPFList, authDetails?.unit_fullname, authDetails?.unit_address, "Section Wage Register Report", columns)}>
                          <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}
                  <Button type="reset" className="btn button-cancel ms-2"
                    onClick={() => {
                      Swal.fire({
                        title: "Are you sure?", text: "Unsaved clear will be lost!", icon: "warning",
                        showCancelButton: true, confirmButtonText: "Yes, Clear", cancelButtonText: "No", confirmButtonColor: "#3085d6", cancelButtonColor: "#d33"
                      }).then((result) => {
                        if (result.isConfirmed) {
                          formik.handleReset();
                          dispatch(clearMonthlyPFList());
                        }
                      });
                    }}
                  >
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <TableContainerColumnTotal columns={columns} data={monthlyPFList || []} isGlobalFilter={true} customPageSize={10} />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default PayrollReportMonthlyPF;